import React, { useMemo, useCallback } from 'react';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";
import { QRCodeCanvas } from 'qrcode.react';
import { FONT_FAMILY } from 'common/sharedFunctions';
import AppleIcon from '@mui/icons-material/Apple';
import AndroidIcon from '@mui/icons-material/Android';

const AppDownloadBanner = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const settings = useSelector(state => state.settingsdata.settings);
  const { t, i18n } = useTranslation();
  
  const lang = useMemo(() => {
    const rawLang = i18n?.language || i18n?.languages?.[0] || 'en';
    return rawLang.toLowerCase().startsWith('fr') ? 'fr' : 'en';
  }, [i18n?.language, i18n?.languages]);

  const localTranslations = useMemo(() => ({
    en: {
      download_title: 'Book from the app',
      download_subtitle: 'Scan the QR code to download the app, check your fare and request a ride.',
      ios: 'iOS',
      android: 'Android'
    },
    fr: {
      download_title: 'Réservez depuis l’application',
      download_subtitle: 'Scannez le QR code pour télécharger l’application, consulter le tarif et demander une course.',
      ios: 'iOS',
      android: 'Android'
    }
  }), []);

  const localT = useCallback((key) => {
    try {
      const v = t(key);
      if (v && v !== key) return v;
    } catch (e) {}
    return localTranslations[lang]?.[key] || localTranslations.en[key] || '';
  }, [t, localTranslations, lang]);

  const QRCard = ({ link, label, Icon }) => (
    <Box
      id="app-download"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1.5,
        padding: { xs: '16px', md: '20px' },
        borderRadius: '16px',
        backgroundColor: 'rgba(255, 255, 255, 0.03)',
        border: '1px solid rgba(212, 175, 55, 0.15)',
        backdropFilter: 'blur(10px)',
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.06)',
          borderColor: 'rgba(212, 175, 55, 0.4)',
          transform: 'translateY(-4px)',
          boxShadow: '0 12px 40px rgba(212, 175, 55, 0.15)'
        }
      }}
      component="a"
      href={link}
      target="_blank"
      rel="noopener noreferrer"
    >
      {/* QR Code */}
      <Box sx={{ 
        background: 'white', 
        padding: '12px', 
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
      }}>
        <QRCodeCanvas 
          value={link || 'https://allodriver.app'} 
          size={isMobile ? 100 : 120}
          level="H"
          includeMargin={false}
        />
      </Box>
      
      {/* Label with Icon */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        padding: '8px 16px',
        borderRadius: '20px',
        backgroundColor: 'rgba(212, 175, 55, 0.1)',
        border: '1px solid rgba(212, 175, 55, 0.2)'
      }}>
        <Icon sx={{ fontSize: 18, color: '#d4af37' }} />
        <Typography sx={{ 
          color: '#fff', 
          fontSize: '14px', 
          fontWeight: 600,
          fontFamily: FONT_FAMILY
        }}>
          {label}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        width: '100%',
        py: { xs: 4, md: 5 },
        px: { xs: 2, md: 4 },
        background: 'linear-gradient(180deg, rgba(0,0,0,0.95) 0%, rgba(10,10,10,1) 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Subtle glow effect */}
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '600px',
        height: '300px',
        borderRadius: '50%',
        background: 'radial-gradient(ellipse, rgba(212, 175, 55, 0.08) 0%, transparent 60%)',
        filter: 'blur(60px)',
        pointerEvents: 'none'
      }} />

      <Box
        sx={{
          maxWidth: '1000px',
          margin: '0 auto',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'center',
          justifyContent: 'center',
          gap: { xs: 3, md: 6 },
          position: 'relative',
          zIndex: 1
        }}
      >
        {/* Text Content */}
        <Box sx={{ 
          textAlign: { xs: 'center', md: 'left' },
          flex: { md: '0 0 auto' },
          maxWidth: { md: '350px' }
        }}>
          <Typography
            variant="h4"
            sx={{
              color: '#fff',
              fontWeight: 700,
              fontFamily: FONT_FAMILY,
              fontSize: { xs: '24px', md: '32px' },
              mb: 1.5,
              lineHeight: 1.2
            }}
          >
            {localT('download_title')}
          </Typography>
          <Typography
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: { xs: '14px', md: '16px' },
              lineHeight: 1.6,
              fontFamily: '"Inter", sans-serif'
            }}
          >
            {localT('download_subtitle')}
          </Typography>
        </Box>

        {/* QR Codes */}
        <Box sx={{ 
          display: 'flex', 
          gap: { xs: 2, md: 3 },
          flexDirection: 'row'
        }}>
          <QRCard 
            link={settings?.AppleStoreLink || 'https://apps.apple.com'} 
            label={localT('ios')}
            Icon={AppleIcon}
          />
          <QRCard 
            link={settings?.PlayStoreLink || 'https://play.google.com'} 
            label={localT('android')}
            Icon={AndroidIcon}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default React.memo(AppDownloadBanner);
